import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import LoadingScreen from '../components/LoadingScreen';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import CustomerTicketsList from '../pages/tickets';
import CustomerTicketDetails from '../pages/ticket';

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> }
      ]
    },
    // Dashboard Routes
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: 'admin', element: <AdminDashboard /> },
        { path: 'admin/import-qa/:userId', element: <ImportQaProduct /> },
        { path: 'admin/processing-verify/:userId', element: <ManualTicketVerify /> },
        { path: 'admin/delete-old-logs', element: <DeleteOldLogs /> },
        { path: 'admin/processing-logs', element: <ProcessingLogs /> },
        { path: 'admin/processing-entry/:logId/:userId', element: <AdminProcessingEntry /> },
        { path: 'admin/new-feature-usage', element: <NewFeatureUsagePage /> },
        { path: 'admin/global-stats', element: <GlobalStats /> },
        { element: <Navigate to="/home" replace />, index: true },
        { path: 'home', element: <HomePage /> },
        { path: 'trial', element: <TrialZone /> },
        { path: 'qa', element: <QA /> },
        { path: 'add-ticket-policy', element: <AddTicketPolicy /> },
        { path: 'tov', element: <BotCharacter /> },
        { path: 'bot-list', element: <ToneOfVoice /> },
        { path: 'settings/general', element: <Settings /> },
        { path: 'settings/connectors', element: <SettingsConnectors /> },
        { path: 'settings/actions', element: <SettingsActions /> },
        { path: 'settings/automation', element: <SettingsAutomation /> },
        { path: 'settings/products', element: <ProductSettings /> },
        { path: 'settings/billing', element: <SettingsBilling /> },
        { path: 'settings/plan-select', element: <PlanSelect /> },
        {
          path: 'testing',
          element: <TestingDash />
        },
        { path: 'create-test', element: <CreateTestCase /> },
        { path: 'processing', element: <ProcessingDash /> },
        { path: 'usage', element: <UsageTracking /> },
        { path: 'processing-entry/:logId', element: <ProcessingEntry /> },
        { path: 'processing/replacement/check-images/:logId', element: <CheckReplaceImages /> },
        {
          path: 'app',
          children: [
            { element: <Navigate to="/dashboard/app/four" replace /> },
            { path: 'four', element: <PageFour /> },
            { path: 'five', element: <PageFive /> },
            { path: 'six', element: <PageSix /> }
          ]
        },
        { path: 'tickets', element: <CustomerTicketsList /> },
        { path: 'ticket/:logId', element: <CustomerTicketDetails /> }
      ]
    },
    {
      path: '/service_oauth/callback/:service',
      element: (
        <AuthGuard>
          <OAuthCallback />
        </AuthGuard>
      )
    },
    { path: '/optin/:ticket_log_id', element: <OptInDecision /> },
    { path: '/shopify', element: <ConnectShopifyMarketplace /> },
    { path: '/register-callback-shopify', element: <AuthorizeGuestVennie /> },
    { path: '/terms-and-privacy', element: <TermsAndPrivacy /> },
    { path: '/report-wrong/:link_type/:processing_entry_id/:answer_action_id', element: <ReportWrongPage /> },
    { path: '/welcome', element:  <Onboarding /> },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}


// Admin
const AdminDashboard = Loadable(lazy(() => import('../pages/admin/AdminDashboard')));
const ImportQaProduct = Loadable(lazy(() => import('../pages/admin/ImportQaProduct')));
const ManualTicketVerify = Loadable(lazy(() => import('../pages/admin/ManualTicketVerify')));
const DeleteOldLogs = Loadable(lazy(() => import('../pages/admin/DeleteOldLogs')));
const ProcessingLogs = Loadable(lazy(() => import('../pages/admin/ProcessingLogs')));
const AdminProcessingEntry = Loadable(lazy(() => import('../pages/admin/AdminProcessingEntry')));
const NewFeatureUsagePage = Loadable(lazy(() => import('../pages/admin/NewFeatureUsagePage')));
const GlobalStats = Loadable(lazy(() => import('../pages/admin/GlobalStats')));
// Dashboard
const HomePage = Loadable(lazy(() => import('../pages/dashboard/home/HomePage')));
const WelcomePage = Loadable(lazy(() => import('../pages/dashboard/welcome/WelcomePage')));
const ToneOfVoice = Loadable(lazy(() => import('../pages/dashboard/ToneOfVoice')));
const BotCharacter = Loadable(lazy(() => import('../pages/dashboard/BotCharacter')));
const PageFour = Loadable(lazy(() => import('../pages/PageFour')));
const PageFive = Loadable(lazy(() => import('../pages/PageFive')));
const PageSix = Loadable(lazy(() => import('../pages/PageSix')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const Statistics = Loadable(lazy(() => import('../pages/dashboard/Statistics')));
const OAuthCallback = Loadable(lazy(() => import('../pages/OauthCallback')));
const Settings = Loadable(lazy(() => import('../pages/dashboard/Settings')));
const SettingsConnectors = Loadable(lazy(() => import('../pages/dashboard/Settings/connectors')));
const SettingsActions = Loadable(lazy(() => import('../pages/dashboard/actions/ActionsPage')));
const SettingsBilling = Loadable(lazy(() => import('../pages/dashboard/Settings/billing/SettingsBilling')));
const SettingsAutomation = Loadable(lazy(() => import('../pages/dashboard/Settings/AutomationSettings')));
const ProductSettings = Loadable(lazy(() => import('../pages/dashboard/Settings/ProductSettings')));
const TrialZone = Loadable(lazy(() => import('../pages/dashboard/TrialZone')));
const QA = Loadable(lazy(() => import('../pages/dashboard/Faq')));
const AddTicketPolicy = Loadable(lazy(() => import('../pages/dashboard/AddTicketPolicy')));
const PlanSelect = Loadable(lazy(() => import('../pages/dashboard/PlanSelect')));
const OptInDecision = Loadable(lazy(() => import('../pages/dashboard/optIn/OptInDecision')));
const TermsAndPrivacy = Loadable(lazy(() => import('../pages/TermsAndPrivacy')));
const Onboarding = Loadable(lazy(() => import('../pages/dashboard/onboarding/WelcomePage')));

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/authentication/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));

// Processing
const TestingDash = Loadable(lazy(() => import('../pages/testing/dash')));
const CreateTestCase = Loadable(lazy(() => import('../pages/testing/create')));
const ProcessingDash = Loadable(lazy(() => import('../pages/processing/dash')));
const ProcessingEntry = Loadable(lazy(() => import('../pages/processing/entry')));
const CheckReplaceImages = Loadable(lazy(() => import('../pages/processing/replacement/check-images')));
const ReportWrongPage = Loadable(lazy(() => import('../pages/processing/suggestion/ReportWrongPage')));
const UsageTracking = Loadable(lazy(() => import('../pages/processing/usage/UsageTrackingPage')));

// Shopify
const ConnectShopifyMarketplace = Loadable(lazy(() => import('../pages/ConnectShopifyMarketplace')));
const AuthorizeGuestVennie = Loadable(lazy(() => import('../components/shopify/AuthorizeGuestVennie')));

